import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Collapse,
  Input,
  InputNumber,
  Select,
} from "antd";
import { useQuery } from "@tanstack/react-query";
import { connect } from "react-redux";
import { orderBy } from "lodash";
import { MinusOutlined } from "@ant-design/icons/es/icons";
import { PlusOutlined } from "@ant-design/icons";
import BannerTop from "../../components/BannerTop/BannerTop";
import Tree from "../../components/Tree/Tree";
import Label from "../../components/Label/Label";
import Localize, { l } from "../../libs/localization";
import CompanyAutocomplete from "../../components/CompanyAutocomplete/CompanyAutocomplete";
import { getTreeDataV2Api } from "../../api/registry.api";
import BrandAdvertisersTable from "../../components/BrandAdvertisers/BrandAdvertisersTable";

const { Panel } = Collapse;

const BrandAdvertisersSearch = () => {
  const [filters, setFilters] = useState({
    sector: [],
    companyIds: [],
    page: 0,
    pageSize: 20,
  });

  const [reachFilter, setReachFilter] = useState({});

  const sectorsQuery = useQuery({
    queryKey: ["sectors"],
    queryFn: () => getTreeDataV2Api({ entity: "Sector" }),
  });

  return (
    <>
      <BannerTop small>
        <h1 className="mt-4 orange-title">
          {Localize("PAGES.BRAND_ADVERTISERS.TITLE")}
        </h1>
      </BannerTop>
      <div className="searchPage__filters animation fadein-up slow \">
        <Card
          bordered={false}
          style={{ borderRadius: "10px" }}
          className="companyPage__body-card companyPage__body-card--custom customCard"
        >
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Label
                className="filter-color"
                value={Localize("PAGES.SEARCH.COMPANIES")}
              />
              <CompanyAutocomplete
                onChange={(value) => {
                  setFilters({
                    ...filters,
                    companyIds: value.map((e) => e?.id),
                    page: 0,
                  });
                }}
              />
            </Col>
            <Col span={8}>
              <Label
                className="filter-color"
                value={Localize("PAGES.SEARCH.ORDER.SECTOR")}
              />
              <Tree
                translateEntity="Sector"
                valueData={filters.sector}
                datatree={orderBy(sectorsQuery?.data || [], ["key"], ["asc"])}
                onChange={(value) => {
                  setFilters({ ...filters, sector: value, page: 0 });
                }}
                onSelect={(value, e) => {
                  setFilters({ ...filters, sector: value, page: 0 });
                }}
              />
            </Col>
            <Col span={8}>
              <Label
                className="filter-color"
                value={Localize("PAGES.SEARCH.PAYER")}
              />
              <Input
                value={filters.payer}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    payer: e.target.value,
                    page: 0,
                  });
                }}
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Label
                className="filter-color"
                value={Localize("BRAND_ADVERTISERS.PLATFORM")}
              />
              <Select
                mode="multiple"
                allowClear={true}
                style={{ width: "100%" }}
                value={filters.advertiserType}
                onChange={(value) =>
                  setFilters({
                    ...filters,
                    advertiserType: value,
                    page: 0,
                  })
                }
                showSearch
                options={[
                  {
                    key: "META",
                    value: "META",
                    label: Localize("BRAND_ADVERTISERS.PLATFORM_META"),
                  },
                  {
                    key: "GOOGLE",
                    value: "GOOGLE",
                    label: Localize("BRAND_ADVERTISERS.PLATFORM_GOOGLE"),
                  },
                ]}
              />
            </Col>
          </Row>
          <Collapse ghost={true} style={{ marginLeft: "-15px" }}>
            <Panel key="1" header={l("PAGES.SEARCH.REACH")}>
              {filters.reach?.map((row, i) => {
                return (
                  <Row gutter={16}>
                    <Col span={6}>
                      {Localize("COMMON.YEAR")}: {row.year}
                    </Col>
                    <Col span={6}>
                      {row.minReach
                        ? `${l("PAGES.SEARCH.REACH_MINIMUM")}: ${row.minReach}`
                        : ""}
                    </Col>

                    <Col span={6}>
                      {row.maxReach
                        ? `${l("PAGES.SEARCH.REACH_MAXIMUM")}: ${row.maxReach}`
                        : ""}
                    </Col>
                    <Col span={6}>
                      <Button
                        type="default"
                        onClick={() => {
                          setFilters({
                            ...filters,
                            reach: filters.reach?.filter((_, j) => j !== i),
                            page: 0,
                          });
                        }}
                      >
                        <MinusOutlined />
                        {l("COMMON.REMOVE")}
                      </Button>
                    </Col>
                  </Row>
                );
              })}
              <Row gutter={16}>
                <Col span={6}>
                  <Label className="filter-color" value={l("COMMON.YEAR")} />
                  <InputNumber
                    style={{ width: "100%" }}
                    min={2000}
                    max={new Date().getFullYear()}
                    value={reachFilter.year}
                    onChange={(value) =>
                      setReachFilter({ ...reachFilter, year: value })
                    }
                  />
                </Col>
                <Col span={6}>
                  <Label
                    className="filter-color"
                    value={Localize("PAGES.SEARCH.REACH_MINIMUM")}
                  />
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    value={reachFilter.minReach}
                    onChange={(value) =>
                      setReachFilter({ ...reachFilter, minReach: value })
                    }
                  />
                </Col>
                <Col span={6}>
                  <Label
                    className="filter-color"
                    value={Localize("PAGES.SEARCH.REACH_MAXIMUM")}
                  />
                  <InputNumber
                    style={{ width: "100%" }}
                    min={reachFilter.minReach || 0}
                    value={reachFilter.maxReach}
                    onChange={(value) =>
                      setReachFilter({ ...reachFilter, maxReach: value })
                    }
                  />
                </Col>
                <Col span={6}>
                  <Button
                    disabled={
                      !reachFilter.year ||
                      reachFilter.minReach > reachFilter.maxReach
                    }
                    type="primary"
                    onClick={() => {
                      setFilters({
                        ...filters,
                        reach: [...(filters.reach || []), { ...reachFilter }],
                        page: 0,
                      });
                      setReachFilter({});
                    }}
                    style={{ marginTop: 36 }}
                  >
                    <PlusOutlined />
                    {Localize("COMMON.ADD")}
                  </Button>
                </Col>
              </Row>
            </Panel>
          </Collapse>
        </Card>
      </div>
      <BrandAdvertisersTable filters={filters} onFiltersChange={setFilters} />
    </>
  );
};

const mapStateToProps = ({ auth }) => ({
  user: auth.user,
});

export default connect(mapStateToProps, {})(BrandAdvertisersSearch);
